import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faSearch, 
  faPlus, 
  faEdit, 
  faTrash, 
  faBan, 
  faCheck, 
  faFileExcel,
  faSpinner,
  faFilter,
  faTimes,
  faUser,
  faPhone,
  faEnvelope,
  faTag,
  faChartBar
} from "@fortawesome/free-solid-svg-icons";
import { useAseguradosService } from '../services/asegurados.service';
import { useAuth } from '../services/auth.service';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Button, 
  TextField, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  IconButton,
  Chip,
  Avatar,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  Alert,
  Snackbar,
  LinearProgress,
  Tooltip,
  Fab
} from '@mui/material';
import { styled } from '@mui/material/styles';

// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  borderRadius: '16px',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)',
  },
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: '12px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  overflow: 'hidden',
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  backgroundColor: '#f8f9fa',
  borderBottom: '2px solid #e9ecef',
}));

const SearchContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  backgroundColor: '#f8f9fa',
  borderRadius: '12px',
  padding: '8px 16px',
  border: '1px solid #e9ecef',
  transition: 'all 0.3s ease',
}));

export default function Asegurados() {
  const [asegurados, setAsegurados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    overview: {},
    executives: [],
    tags: []
  });
  const [pagination, setPagination] = useState({
    skip: 0,
    limit: 20,
    total: 0,
    hasMore: false,
    currentPage: 1,
    totalPages: 0
  });
  const [search, setSearch] = useState('');
  const [selectedAsegurado, setSelectedAsegurado] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState('create'); // 'create' or 'edit'
  const [formData, setFormData] = useState({
    NombreCompleto: '',
    Celular: '',
    Email: '',
    ejecutivoAsignado: [],
    etiquetas: []
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const aseguradosService = useAseguradosService();
  const { user } = useAuth();

  // Load asegurados
  const loadAsegurados = async () => {
    setLoading(true);
    try {
      const data = await aseguradosService.getAsegurados(
        pagination.skip, 
        pagination.limit, 
        search
      );
      
      // Handle different response structures
      let aseguradosArray = [];
      let totalCount = 0;
      
      console.log('API Response:', data); // Debug log
      
      if (data) {
        if (Array.isArray(data)) {
          aseguradosArray = data;
          totalCount = data.length;
        } else if (data.asegurados && Array.isArray(data.asegurados)) {
          aseguradosArray = data.asegurados;
          totalCount = data.pagination?.total || data.total || data.asegurados.length;
        } else if (data.data && Array.isArray(data.data)) {
          aseguradosArray = data.data;
          totalCount = data.pagination?.total || data.total || data.data.length;
        } else {
          console.warn('Unexpected data structure:', data);
          aseguradosArray = [];
          totalCount = 0;
        }
      }
      
      setAsegurados(aseguradosArray);
      setPagination(prev => ({ 
        ...prev, 
        total: totalCount,
        hasMore: data?.pagination?.hasMore || false,
        currentPage: data?.pagination?.currentPage || 1,
        totalPages: data?.pagination?.totalPages || Math.ceil(totalCount / prev.limit)
      }));
    } catch (error) {
      console.error('Error loading asegurados:', error);
      showSnackbar('Error loading asegurados', 'error');
      setAsegurados([]);
      setPagination(prev => ({ ...prev, total: 0 }));
    } finally {
      setLoading(false);
    }
  };

  // Load statistics
  const loadStats = async () => {
    try {
      const [overview, executives, tags] = await Promise.all([
        aseguradosService.getStatsOverview(),
        aseguradosService.getStatsExecutives(),
        aseguradosService.getStatsTags()
      ]);
      setStats({ 
        overview: overview || {}, 
        executives: executives || [], 
        tags: tags || [] 
      });
    } catch (error) {
      console.error('Error loading stats:', error);
      setStats({ overview: {}, executives: [], tags: [] });
    }
  };

  // Show snackbar
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  // Handle search
  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPagination(prev => ({ ...prev, skip: 0 }));
  };

  // Handle pagination
  const handlePageChange = (event, page) => {
    setPagination(prev => ({ ...prev, skip: (page - 1) * prev.limit }));
  };

  // Open dialog
  const openDialog = (type, asegurado = null) => {
    setDialogType(type);
    if (type === 'edit' && asegurado) {
      setSelectedAsegurado(asegurado);
      setFormData({
        NombreCompleto: asegurado.NombreCompleto || '',
        Celular: asegurado.Celular || '',
        Email: asegurado.Email || '',
        ejecutivoAsignado: asegurado.ejecutivoAsignado || [],
        etiquetas: asegurado.etiquetas || []
      });
    } else {
      setSelectedAsegurado(null);
      setFormData({
        NombreCompleto: '',
        Celular: '',
        Email: '',
        ejecutivoAsignado: [],
        etiquetas: []
      });
    }
    setDialogOpen(true);
  };

  // Handle form submission
  const handleSubmit = async () => {
    try {
      if (dialogType === 'create') {
        await aseguradosService.createAsegurado(formData);
        showSnackbar('Asegurado created successfully');
      } else {
        await aseguradosService.updateAsegurado(selectedAsegurado.Celular, formData);
        showSnackbar('Asegurado updated successfully');
      }
      setDialogOpen(false);
      loadAsegurados();
    } catch (error) {
      showSnackbar('Error saving asegurado', 'error');
    }
  };

  // Handle block/unblock
  const handleBlock = async (asegurado, bloqueado) => {
    try {
      await aseguradosService.blockAsegurado(asegurado.Celular, bloqueado);
      showSnackbar(`Asegurado ${bloqueado ? 'blocked' : 'unblocked'} successfully`);
      loadAsegurados();
    } catch (error) {
      showSnackbar('Error updating asegurado status', 'error');
    }
  };

  // Handle export
  const handleExport = async () => {
    try {
      const blob = await aseguradosService.exportToExcel({ search });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'asegurados.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
      showSnackbar('Export completed successfully');
    } catch (error) {
      showSnackbar('Error exporting data', 'error');
    }
  };

  // Load data on mount
  useEffect(() => {
    loadAsegurados();
    loadStats();
  }, [pagination.skip, search]);

  // Initialize component
  useEffect(() => {
    // Ensure asegurados is always an array
    if (!Array.isArray(asegurados)) {
      setAsegurados([]);
    }
  }, []);

  return (
    <Box sx={{ p: 3, backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#2c3e50', mb: 1 }}>
          Asegurados
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage your insurance clients and their information
        </Typography>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StyledCard>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {stats.overview.total || 0}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Total Asegurados
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                  <FontAwesomeIcon icon={faUser} size="lg" />
                </Avatar>
              </Box>
            </CardContent>
          </StyledCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StyledCard sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {stats.overview.active || 0}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Active Clients
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                  <FontAwesomeIcon icon={faCheck} size="lg" />
                </Avatar>
              </Box>
            </CardContent>
          </StyledCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StyledCard sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {stats.overview.blocked || 0}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Blocked Clients
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                  <FontAwesomeIcon icon={faBan} size="lg" />
                </Avatar>
              </Box>
            </CardContent>
          </StyledCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StyledCard sx={{ background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {stats.executives?.length || 0}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Executives
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                  <FontAwesomeIcon icon={faChartBar} size="lg" />
                </Avatar>
              </Box>
            </CardContent>
          </StyledCard>
        </Grid>
      </Grid>

      {/* Search and Actions */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <SearchContainer sx={{ flex: 1, maxWidth: 400 }}>
          <FontAwesomeIcon icon={faSearch} style={{ color: '#6c757d', marginRight: '8px' }} />
          <TextField
            fullWidth
            placeholder="Search asegurados..."
            value={search}
            onChange={handleSearch}
            variant="standard"
            InputProps={{ disableUnderline: true }}
          />
        </SearchContainer>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<FontAwesomeIcon icon={faFileExcel} />}
            onClick={handleExport}
            sx={{ borderRadius: '8px' }}
          >
            Export
          </Button>
          <Button
            variant="contained"
            startIcon={<FontAwesomeIcon icon={faPlus} />}
            onClick={() => openDialog('create')}
            sx={{ 
              borderRadius: '8px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
              }
            }}
          >
            New Asegurado
          </Button>
        </Box>
      </Box>

      {/* Table */}
      <StyledTableContainer component={Paper}>
        {loading && <LinearProgress />}
        <Table>
          <TableHead>
            <TableRow>
              <StyledTableCell>Name</StyledTableCell>
              <StyledTableCell>Phone</StyledTableCell>
              <StyledTableCell>Email</StyledTableCell>
              <StyledTableCell>Executive</StyledTableCell>
              <StyledTableCell>Tags</StyledTableCell>
              <StyledTableCell>Status</StyledTableCell>
              <StyledTableCell align="center">Actions</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
                      {loading ? (
            <TableRow>
              <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                  <FontAwesomeIcon icon={faSpinner} spin />
                  <Typography>Loading asegurados...</Typography>
                </Box>
              </TableCell>
            </TableRow>
          ) : (Array.isArray(asegurados) ? asegurados : []).length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                  <FontAwesomeIcon icon={faUser} size="2x" style={{ color: '#ccc' }} />
                  <Typography variant="body1" color="text.secondary">
                    No asegurados found
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {search ? 'Try adjusting your search criteria' : 'Create your first asegurado using the button above'}
                  </Typography>
                </Box>
              </TableCell>
            </TableRow>
          ) : (
            (Array.isArray(asegurados) ? asegurados : []).map((asegurado) => (
              <TableRow key={asegurado.Celular} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: '#667eea' }}>
                      {asegurado.NombreCompleto?.charAt(0)?.toUpperCase()}
                    </Avatar>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {asegurado.NombreCompleto}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FontAwesomeIcon icon={faPhone} style={{ color: '#6c757d' }} />
                    {asegurado.Celular}
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FontAwesomeIcon icon={faEnvelope} style={{ color: '#6c757d' }} />
                    {asegurado.Email}
                  </Box>
                </TableCell>
                <TableCell>
                  {Array.isArray(asegurado.ejecutivoAsignado) ? 
                    asegurado.ejecutivoAsignado.map((exec, index) => (
                      <Chip
                        key={index}
                        label={exec.label}
                        size="small"
                        sx={{ mr: 0.5, bgcolor: '#e3f2fd' }}
                      />
                    )) : 
                    asegurado.ejecutivoAsignado ? (
                      <Chip
                        label={typeof asegurado.ejecutivoAsignado === 'string' ? asegurado.ejecutivoAsignado : asegurado.ejecutivoAsignado.label || asegurado.ejecutivoAsignado}
                        size="small"
                        sx={{ mr: 0.5, bgcolor: '#e3f2fd' }}
                      />
                    ) : null
                  }
                </TableCell>
                <TableCell>
                  {Array.isArray(asegurado.etiquetas) ? 
                    asegurado.etiquetas.map((tag, index) => (
                      <Chip
                        key={index}
                        label={tag.label}
                        size="small"
                        sx={{ mr: 0.5, bgcolor: '#f3e5f5' }}
                      />
                    )) : 
                    asegurado.etiquetas ? (
                      <Chip
                        label={typeof asegurado.etiquetas === 'string' ? asegurado.etiquetas : asegurado.etiquetas.label || asegurado.etiquetas}
                        size="small"
                        sx={{ mr: 0.5, bgcolor: '#f3e5f5' }}
                      />
                    ) : null
                  }
                </TableCell>
                <TableCell>
                  <Chip
                    label={asegurado.bloqueado ? 'Blocked' : 'Active'}
                    color={asegurado.bloqueado ? 'error' : 'success'}
                    size="small"
                  />
                </TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => openDialog('edit', asegurado)}
                        sx={{ color: '#667eea' }}
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={asegurado.bloqueado ? 'Unblock' : 'Block'}>
                      <IconButton
                        size="small"
                        onClick={() => handleBlock(asegurado, !asegurado.bloqueado)}
                        sx={{ color: asegurado.bloqueado ? '#28a745' : '#dc3545' }}
                      >
                        <FontAwesomeIcon icon={asegurado.bloqueado ? faCheck : faBan} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))
          )}
          </TableBody>
        </Table>
      </StyledTableContainer>

      {/* Pagination */}
      {pagination.total > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={pagination.totalPages || Math.ceil(pagination.total / pagination.limit)}
            page={pagination.currentPage || Math.floor(pagination.skip / pagination.limit) + 1}
            onChange={handlePageChange}
            color="primary"
            shape="rounded"
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogType === 'create' ? 'New Asegurado' : 'Edit Asegurado'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              fullWidth
              label="Full Name"
              value={formData.NombreCompleto}
              onChange={(e) => setFormData({ ...formData, NombreCompleto: e.target.value })}
            />
            <TextField
              fullWidth
              label="Phone"
              value={formData.Celular}
              onChange={(e) => setFormData({ ...formData, Celular: e.target.value })}
            />
            <TextField
              fullWidth
              label="Email"
              value={formData.Email}
              onChange={(e) => setFormData({ ...formData, Email: e.target.value })}
            />
            <TextField
              fullWidth
              label="Executive (comma separated)"
              value={Array.isArray(formData.ejecutivoAsignado) ? 
                formData.ejecutivoAsignado.map(e => e.label || e).join(', ') : 
                formData.ejecutivoAsignado || ''}
              onChange={(e) => setFormData({
                ...formData,
                ejecutivoAsignado: e.target.value.split(',').map(label => ({ label: label.trim() }))
              })}
            />
            <TextField
              fullWidth
              label="Tags (comma separated)"
              value={Array.isArray(formData.etiquetas) ? 
                formData.etiquetas.map(t => t.label || t).join(', ') : 
                formData.etiquetas || ''}
              onChange={(e) => setFormData({
                ...formData,
                etiquetas: e.target.value.split(',').map(label => ({ label: label.trim() }))
              })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {dialogType === 'create' ? 'Create' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
} 